import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { 
  Plus, Trash2, Save, LogOut, Package, Eye, EyeOff,
  Upload, X, Clock, DollarSign, Users, Home, Settings,
  FileText, Instagram, Phone, Mail, MapPin, MessageCircle, Image, Columns3, Images
} from "lucide-react";
import RichTextEditor from "../components/RichTextEditor";
import InstagramPostsEditor from "../components/InstagramPostsEditor";
import ChangePasswordBox from "../components/ChangePasswordBox";
import ButtonTextsEditor from "../components/ButtonTextsEditor";
import { stripHtml } from "../lib/text";
import { EKKO_DEFAULTS } from "../lib/ekkoDefaults";
import PillarsTab from "../components/admin/PillarsTab";
import GalleryTab from "../components/admin/GalleryTab";
import LeadFormEditor from "../components/admin/LeadFormEditor";
import HeroExtrasEditor from "../components/admin/HeroExtrasEditor";
import TestimonialsFaqTab from "../components/admin/TestimonialsFaqTab";
import FontPicker from "../components/admin/FontPicker";

// Texto a cinzento num campo vazio: o que o site mostra por omissão
const ph = (key) => stripHtml(EKKO_DEFAULTS[key] || "");

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Tab components
const TABS = [
  { id: "branding", label: "Imagens & Branding", icon: Image },
  { id: "pillars", label: "Pilares & Valores", icon: Columns3 },
  { id: "gallery", label: "Galeria", icon: Images },
  { id: "testimonials", label: "Testemunhos & FAQ", icon: MessageCircle },
  { id: "schedule", label: "Horários", icon: Clock },
  { id: "prices", label: "Preços", icon: DollarSign },
  { id: "coaches", label: "Equipa", icon: Users },
  { id: "content", label: "Textos", icon: FileText },
  { id: "layout", label: "Layout", icon: Settings },
  { id: "users", label: "Utilizadores", icon: Users },
  { id: "contact", label: "Contacto", icon: Phone },
];

// Secções reordenáveis da página inicial (chave -> nome legível)
const SECTION_META = [
  { key: "hero", label: "Topo (Hero)" },
  { key: "values", label: "Faixa de valores" },
  { key: "pillars", label: "Os três pilares" },
  { key: "schedule", label: "Horários" },
  { key: "coaches", label: "Equipa" },
  { key: "testimonials", label: "Testemunhos" },
  { key: "pricing", label: "Preços" },
  { key: "gallery", label: "Galeria do espaço" },
  { key: "faq", label: "Perguntas frequentes" },
  { key: "instagram", label: "Instagram" },
  { key: "contact", label: "Aula experimental" },
];

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("branding");

  // Reposição de password
  const [showReset, setShowReset] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Utilizadores
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: "", password: "" });
  
  // Data states
  const [coaches, setCoaches] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [prices, setPrices] = useState({});
  const [content, setContent] = useState({});
  
  // Modal states
  const [editingCoach, setEditingCoach] = useState(null);
  const [showAddCoach, setShowAddCoach] = useState(false);
  const [newCoach, setNewCoach] = useState({ nome: "", role: "", frase: "", image: "", active: true, order: 0 });

  useEffect(() => {
    const storedToken = localStorage.getItem("adminToken");
    if (storedToken) {
      setIsAuthenticated(true);
      loadAllData(storedToken);
    }
  }, []);

  const getAuthHeader = useCallback(() => {
    const storedToken = localStorage.getItem("adminToken");
    return storedToken ? `Bearer ${storedToken}` : "";
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password })
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("adminToken", data.token);
        setIsAuthenticated(true);
        toast.success("Login bem sucedido!");
        loadAllData(data.token);
      } else {
        toast.error("Credenciais inválidas");
      }
    } catch (error) {
      toast.error("Erro ao fazer login");
    }
    setIsLoading(false);
  };

  const handleForgot = async () => {
    if (!username) {
      toast.error("Escreve primeiro o teu utilizador.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });
      const data = await response.json();
      if (response.ok) {
        setResetRequested(true);
        toast.success(data.message || "Código enviado.");
      } else {
        toast.error(data.detail || "Erro ao pedir código.");
      }
    } catch (error) {
      toast.error("Erro de ligação.");
    }
    setIsLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, code: resetCode, new_password: newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || "Password alterada.");
        setShowReset(false);
        setResetRequested(false);
        setResetCode("");
        setNewPassword("");
      } else {
        toast.error(data.detail || "Erro ao repor password.");
      }
    } catch (error) {
      toast.error("Erro de ligação.");
    }
    setIsLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setIsAuthenticated(false);
    setUsername("");
    setPassword("");
  };

  const handleAuthError = () => {
    toast.error("Sessão expirada. Por favor, faça login novamente.");
    handleLogout();
  };

  const loadAllData = async (token) => {
    const headers = { "Authorization": `Bearer ${token}` };
    
    try {
      const [coachesRes, scheduleRes, pricesRes, contentRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/coaches`, { headers }),
        fetch(`${API_URL}/api/schedule`),
        fetch(`${API_URL}/api/prices`),
        fetch(`${API_URL}/api/content`)
      ]);
      
      // Check for auth errors
      if (coachesRes.status === 401) {
        handleAuthError();
        return;
      }
      
      if (coachesRes.ok) setCoaches(await coachesRes.json());
      if (scheduleRes.ok) setSchedule(await scheduleRes.json());
      if (pricesRes.ok) setPrices(await pricesRes.json());
      if (contentRes.ok) setContent(await contentRes.json());
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erro ao carregar dados");
    }
  };

  const handleImageUpload = async (e, callback) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagem muito grande. Máximo 5MB.");
      return;
    }
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      toast.info("A carregar imagem...");
      const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: "POST",
        headers: { "Authorization": getAuthHeader() },
        body: formData
      });
      
      if (response.status === 401) { 
        handleAuthError(); 
        return; 
      }
      if (response.ok) {
        const data = await response.json();
        // O Cloudinary devolve um URL absoluto; só juntamos o backend se for relativo
        const finalUrl = data.url && data.url.startsWith("http") ? data.url : `${API_URL}${data.url}`;
        callback(finalUrl);
        toast.success("Imagem carregada!");
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || "Erro ao carregar imagem");
        console.error("Upload error:", response.status, errorData);
      }
    } catch (error) {
      toast.error("Erro de conexão ao carregar imagem");
      console.error("Upload exception:", error);
    }
  };

  // =====================
  // COACH HANDLERS
  // =====================
  const handleAddCoach = async () => {
    if (!newCoach.nome || !newCoach.role) {
      toast.error("Preencha nome e função");
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/coaches`, {
        method: "POST",
        headers: { "Authorization": getAuthHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ ...newCoach, order: coaches.length })
      });
      
      if (response.status === 401) { handleAuthError(); return; }
      if (response.ok) {
        toast.success("Coach adicionado!");
        setShowAddCoach(false);
        setNewCoach({ nome: "", role: "", frase: "", image: "", active: true, order: 0 });
        loadAllData(localStorage.getItem("adminToken"));
      }
    } catch (error) {
      toast.error("Erro ao adicionar coach");
    }
  };

  const handleUpdateCoach = async (coach) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/coaches/${coach.id}`, {
        method: "PUT",
        headers: { "Authorization": getAuthHeader(), "Content-Type": "application/json" },
        body: JSON.stringify(coach)
      });
      
      if (response.status === 401) { handleAuthError(); return; }
      if (response.ok) {
        toast.success("Coach atualizado!");
        setEditingCoach(null);
        loadAllData(localStorage.getItem("adminToken"));
      }
    } catch (error) {
      toast.error("Erro ao atualizar coach");
    }
  };

  const handleDeleteCoach = async (coachId) => {
    if (!window.confirm("Eliminar este coach?")) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/coaches/${coachId}`, {
        method: "DELETE",
        headers: { "Authorization": getAuthHeader() }
      });
      
      if (response.status === 401) { handleAuthError(); return; }
      if (response.ok) {
        toast.success("Coach eliminado!");
        loadAllData(localStorage.getItem("adminToken"));
      }
    } catch (error) {
      toast.error("Erro ao eliminar coach");
    }
  };

  // =====================
  // SCHEDULE HANDLERS
  // =====================
  const handleSaveSchedule = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/schedule`, {
        method: "PUT",
        headers: { "Authorization": getAuthHeader(), "Content-Type": "application/json" },
        body: JSON.stringify(schedule)
      });
      
      if (response.status === 401) { handleAuthError(); return; }
      if (response.ok) {
        toast.success("Horários guardados!");
      } else {
        toast.error("Erro ao guardar horários");
      }
    } catch (error) {
      toast.error("Erro ao guardar horários");
    }
  };

  // =====================
  // PRICES HANDLERS
  // =====================
  const handleSavePrices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/prices`, {
        method: "PUT",
        headers: { "Authorization": getAuthHeader(), "Content-Type": "application/json" },
        body: JSON.stringify(prices)
      });
      
      if (response.status === 401) { handleAuthError(); return; }
      if (response.ok) {
        toast.success("Preços guardados!");
      } else {
        toast.error("Erro ao guardar preços");
      }
    } catch (error) {
      toast.error("Erro ao guardar preços");
    }
  };

  // =====================
  // CONTENT HANDLERS
  // =====================
  const handleSaveContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/content`, {
        method: "PUT",
        headers: { "Authorization": getAuthHeader(), "Content-Type": "application/json" },
        body: JSON.stringify(content)
      });
      
      if (response.status === 401) { handleAuthError(); return; }
      if (response.ok) {
        toast.success("Conteúdo guardado!");
      } else {
        toast.error("Erro ao guardar conteúdo");
      }
    } catch (error) {
      toast.error("Erro ao guardar conteúdo");
    }
  };

  // --- Layout das secções (ordenar / mostrar / esconder) ---
  const getLayoutSections = () => {
    const saved = Array.isArray(content.layout_sections) ? content.layout_sections : [];
    const result = saved.filter((s) => SECTION_META.some((m) => m.key === s.key));
    SECTION_META.forEach((m) => {
      if (!result.some((s) => s.key === m.key)) result.push({ key: m.key, visible: true });
    });
    return result;
  };

  const moveSection = (index, dir) => {
    const list = getLayoutSections();
    const target = index + dir;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    setContent((prev) => ({ ...prev, layout_sections: list }));
  };

  const toggleSection = (index) => {
    const list = getLayoutSections();
    list[index] = { ...list[index], visible: !list[index].visible };
    setContent((prev) => ({ ...prev, layout_sections: list }));
  };

  // --- Utilizadores ---
  const loadUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { "Authorization": getAuthHeader() },
      });
      if (response.status === 401) { handleAuthError(); return; }
      if (response.ok) setUsers(await response.json());
    } catch (error) {
      // silencioso
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password) {
      toast.error("Preenche utilizador e password.");
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        method: "POST",
        headers: { "Authorization": getAuthHeader(), "Content-Type": "application/json" },
        body: JSON.stringify(newUser),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || "Utilizador criado.");
        setNewUser({ username: "", password: "" });
        loadUsers();
      } else {
        toast.error(data.detail || "Erro ao criar utilizador.");
      }
    } catch (error) {
      toast.error("Erro de ligação.");
    }
  };

  const handleDeleteUser = async (target) => {
    if (!window.confirm(`Remover o utilizador "${target}"?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${encodeURIComponent(target)}`, {
        method: "DELETE",
        headers: { "Authorization": getAuthHeader() },
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || "Utilizador removido.");
        loadUsers();
      } else {
        toast.error(data.detail || "Erro ao remover utilizador.");
      }
    } catch (error) {
      toast.error("Erro de ligação.");
    }
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === "users") loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeTab]);

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#141414] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-[#1F1F1F] rounded-lg p-8 border border-[#3A3A3A]">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-[#F5F5F5] mb-2" >
                Painel Admin
              </h1>
              <p className="text-[#A1A1AA] text-sm">EKKO Fitness Center</p>
            </div>
            
            {!showReset ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Utilizador</label>
                <input
                  type="text"
                  name="username"
                  id="username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="O teu utilizador (ex.: marta)"
                  className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-4 py-3 text-[#F5F5F5] focus:border-[#A6C48A] focus:outline-none"
                  data-testid="admin-username"
                />
              </div>
              
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Password</label>
                <input
                  type="password"
                  name="password"
                  id="current-password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="A tua password"
                  className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-4 py-3 text-[#F5F5F5] focus:border-[#A6C48A] focus:outline-none"
                  data-testid="admin-password"
                />
              </div>
              
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] font-bold py-3 rounded transition-colors disabled:opacity-50"
                data-testid="admin-login-btn"
              >
                {isLoading ? "A entrar..." : "Entrar"}
              </button>
            </form>
            ) : (
            <div className="space-y-4">
              <p className="text-[#A1A1AA] text-sm text-center">
                Enviamos um código de reposição para o email da conta.
              </p>
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Utilizador</label>
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="O teu utilizador (ex.: marta)"
                  className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-4 py-3 text-[#F5F5F5] focus:border-[#A6C48A] focus:outline-none"
                />
              </div>
              {!resetRequested ? (
                <button
                  type="button"
                  onClick={handleForgot}
                  disabled={isLoading}
                  className="w-full bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] font-bold py-3 rounded transition-colors disabled:opacity-50"
                >
                  {isLoading ? "A enviar..." : "Enviar código por email"}
                </button>
              ) : (
                <form onSubmit={handleReset} className="space-y-4">
                  <div>
                    <label className="block text-[#A1A1AA] text-sm mb-2">Código (6 dígitos)</label>
                    <input
                      type="text"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      placeholder="Os 6 dígitos do email"
                      inputMode="numeric"
                      className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-4 py-3 text-[#F5F5F5] focus:border-[#A6C48A] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[#A1A1AA] text-sm mb-2">Nova password</label>
                    <input
                      type="password"
                      name="new-password"
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-4 py-3 text-[#F5F5F5] focus:border-[#A6C48A] focus:outline-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] font-bold py-3 rounded transition-colors disabled:opacity-50"
                  >
                    {isLoading ? "A alterar..." : "Alterar password"}
                  </button>
                </form>
              )}
            </div>
            )}
            
            <div className="mt-6 text-center space-y-2">
              {!showReset ? (
                <button type="button" onClick={() => setShowReset(true)} className="block w-full text-[#A1A1AA] hover:text-[#A6C48A] text-sm">
                  Esqueci a password
                </button>
              ) : (
                <button type="button" onClick={() => { setShowReset(false); setResetRequested(false); }} className="block w-full text-[#A1A1AA] hover:text-[#A6C48A] text-sm">
                  ← Voltar ao login
                </button>
              )}
              <a href="/" className="block text-[#A6C48A] hover:text-[#B9D3A1] text-sm">← Voltar ao site</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#141414]">
      {/* Header */}
      <header className="bg-[#1F1F1F] border-b border-[#3A3A3A] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-[#F5F5F5]" >
              Painel Admin
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-[#F5F5F5] text-sm flex items-center gap-2">
              <Eye size={16} /> Ver site
            </a>
            <button onClick={handleLogout} className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#A6C48A]" data-testid="admin-logout-btn">
              <LogOut size={18} /> <span className="text-sm">Sair</span>
            </button>
          </div>
        </div>
      </header>

      {/* Layout: barra lateral + conteúdo */}
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-16 md:w-56 shrink-0 bg-[#1F1F1F]/50 border-r border-[#3A3A3A] sticky top-[65px] self-start h-[calc(100vh-65px)] overflow-y-auto">
          <nav className="flex flex-col gap-1 p-2 md:p-3">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                title={tab.label}
                className={`flex items-center gap-3 px-3 py-2 rounded text-sm font-medium transition-colors whitespace-nowrap justify-center md:justify-start ${
                  activeTab === tab.id
                    ? "bg-[#141414] text-[#A6C48A]"
                    : "text-[#A1A1AA] hover:text-[#F5F5F5] hover:bg-[#141414]/50"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={18} />
                <span className="hidden md:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 px-4 md:px-8 py-8 max-w-5xl min-w-0">
        
        {/* BRANDING TAB */}
        {activeTab === "branding" && (
          <section>
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-[#F5F5F5]" >
                <Image className="inline mr-2 text-[#A6C48A]" size={24} /> Imagens & Branding
              </h2>
              <button onClick={handleSaveContent} className="flex items-center gap-2 bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] px-4 py-2 rounded" data-testid="save-branding-btn">
                <Save size={18} /> Guardar
              </button>
            </div>

            <div className="space-y-6">
              {/* Logo */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-4 flex items-center gap-2">
                  <Image size={18} /> Logo do Site
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-2">URL do Logo</label>
                    <div className="flex gap-2">
                      <input
                        value={content.logo_url || ""}
                        onChange={(e) => setContent(prev => ({ ...prev, logo_url: e.target.value }))}
                        className="flex-1 bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm"
                        placeholder="Vazio = logótipo EKKO original"
                      />
                      <label className="flex items-center gap-2 bg-[#3A3A3A] hover:bg-[#4A4A4A] text-[#F5F5F5] px-4 py-2 rounded cursor-pointer">
                        <Upload size={16} />
                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, (url) => setContent(prev => ({ ...prev, logo_url: url })))} className="hidden" />
                      </label>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    {content.logo_url && (
                      <div className="bg-[#141414] p-4 rounded-lg">
                        <img src={content.logo_url} alt="Logo Preview" className="h-24 object-contain" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Hero Background */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-4 flex items-center gap-2">
                  <Home size={18} /> Imagem de Fundo (Hero)
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-2">URL da Imagem de Fundo</label>
                    <div className="flex gap-2">
                      <input
                        value={content.hero_background_image || ""}
                        onChange={(e) => setContent(prev => ({ ...prev, hero_background_image: e.target.value }))}
                        className="flex-1 bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm"
                        placeholder="Vazio = foto por omissão. Carrega com o botão ao lado ou cola um link https://"
                      />
                      <label className="flex items-center gap-2 bg-[#3A3A3A] hover:bg-[#4A4A4A] text-[#F5F5F5] px-4 py-2 rounded cursor-pointer">
                        <Upload size={16} />
                        <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, (url) => setContent(prev => ({ ...prev, hero_background_image: url })))} className="hidden" />
                      </label>
                    </div>
                    <p className="text-[#8A8A8A] text-xs mt-2">Recomendado: imagem de alta qualidade, mínimo 1920x1080px</p>
                  </div>
                  <div className="flex items-center justify-center">
                    {content.hero_background_image && (
                      <div className="bg-[#141414] p-2 rounded-lg w-full">
                        <img src={content.hero_background_image} alt="Hero Preview" className="w-full h-32 object-cover rounded" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Hero Texts */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-4 flex items-center gap-2">
                  <FileText size={18} /> Textos do Hero
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1">Título Principal</label>
                    <input
                      value={content.hero_title || ""}
                      onChange={(e) => setContent(prev => ({ ...prev, hero_title: e.target.value }))}
                      className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm"
                      placeholder={ph("hero_title")}
                    />
                  </div>
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1">Subtítulo</label>
                    <input
                      value={content.hero_subtitle || ""}
                      onChange={(e) => setContent(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                      className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm"
                      placeholder={ph("hero_subtitle")}
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#A1A1AA] text-xs mb-1">Botão Principal</label>
                      <input
                        value={content.hero_button_primary || ""}
                        onChange={(e) => setContent(prev => ({ ...prev, hero_button_primary: e.target.value }))}
                        className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm"
                        placeholder={ph("hero_button_primary")}
                      />
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-xs mb-1">Botão Secundário</label>
                      <input
                        value={content.hero_button_secondary || ""}
                        onChange={(e) => setContent(prev => ({ ...prev, hero_button_secondary: e.target.value }))}
                        className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm"
                        placeholder={ph("hero_button_secondary")}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <HeroExtrasEditor content={content} setContent={setContent} />

              {/* Instagram */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-4 flex items-center gap-2">
                  <Instagram size={18} /> Instagram
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1">Handle (@utilizador)</label>
                    <input
                      value={content.instagram_handle || ""}
                      onChange={(e) => setContent(prev => ({ ...prev, instagram_handle: e.target.value }))}
                      className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm"
                      placeholder={ph("instagram_handle")}
                    />
                  </div>
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1">URL do Perfil</label>
                    <input
                      value={content.instagram_url || ""}
                      onChange={(e) => setContent(prev => ({ ...prev, instagram_url: e.target.value }))}
                      className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm"
                      placeholder={ph("instagram_url")}
                    />
                  </div>
                </div>
                <InstagramPostsEditor
                  posts={content.instagram_posts || []}
                  onChange={(posts) => setContent(prev => ({ ...prev, instagram_posts: posts }))}
                  onUpload={handleImageUpload}
                />
              </div>
            </div>
          </section>
        )}

        {/* PILARES & VALORES */}
        {activeTab === "pillars" && (
          <section>
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-[#F5F5F5]">
                <Columns3 className="inline mr-2 text-[#A6C48A]" size={24} /> Pilares & Valores
              </h2>
              <button onClick={handleSaveContent} className="flex items-center gap-2 bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] px-4 py-2 rounded">
                <Save size={18} /> Guardar
              </button>
            </div>
            <PillarsTab content={content} setContent={setContent} />
          </section>
        )}

        {/* GALERIA */}
        {activeTab === "gallery" && (
          <section>
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-[#F5F5F5]">
                <Images className="inline mr-2 text-[#A6C48A]" size={24} /> Galeria
              </h2>
              <button onClick={handleSaveContent} className="flex items-center gap-2 bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] px-4 py-2 rounded">
                <Save size={18} /> Guardar
              </button>
            </div>
            <GalleryTab content={content} setContent={setContent} onUpload={handleImageUpload} />
          </section>
        )}

        {/* TESTEMUNHOS & FAQ */}
        {activeTab === "testimonials" && (
          <section>
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-[#F5F5F5]">
                <MessageCircle className="inline mr-2 text-[#A6C48A]" size={24} /> Testemunhos & FAQ
              </h2>
              <button onClick={handleSaveContent} className="flex items-center gap-2 bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] px-4 py-2 rounded">
                <Save size={18} /> Guardar
              </button>
            </div>
            <TestimonialsFaqTab content={content} setContent={setContent} />
          </section>
        )}

        {/* SCHEDULE TAB */}
        {activeTab === "schedule" && (
          <section>
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-[#F5F5F5]" >
                <Clock className="inline mr-2 text-[#A6C48A]" size={24} /> Horários
              </h2>
              <button onClick={handleSaveSchedule} className="flex items-center gap-2 bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] px-4 py-2 rounded" data-testid="save-schedule-btn">
                <Save size={18} /> Guardar
              </button>
            </div>

            <div className="grid gap-4">
              {["segunda", "terca", "quarta", "quinta", "sexta", "sabado", "domingo"].map((day) => (
                <div key={day} className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                  <h3 className="text-[#A6C48A] font-bold mb-3 capitalize">{day === "terca" ? "Terça" : day === "sabado" ? "Sábado" : day.charAt(0).toUpperCase() + day.slice(1)}</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#A1A1AA] text-xs mb-2">Aulas (slots individuais)</label>
                      <textarea
                        value={Array.isArray(schedule[day]?.aulas) 
                          ? schedule[day].aulas.map(a => typeof a === 'string' ? a : a.inicio).join(", ")
                          : ""
                        }
                        onChange={(e) => {
                          const aulas = e.target.value.split(",")
                            .map(s => s.trim())
                            .filter(s => s && /^\d{2}:\d{2}$/.test(s));
                          setSchedule(prev => ({
                            ...prev,
                            [day]: { ...prev[day], aulas }
                          }));
                        }}
                        className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm focus:border-[#A6C48A] focus:outline-none"
                        placeholder="07:00, 07:50, 10:00, 12:45, 17:30"
                        rows={2}
                      />
                      <p className="text-[#8A8A8A] text-xs mt-1">Formato: HH:MM, separados por vírgula</p>
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-xs mb-2">Treino livre (intervalos)</label>
                      <textarea
                        value={schedule[day]?.open_box?.map(a => 
                          a.inicio === a.fim ? a.inicio : `${a.inicio}-${a.fim}`
                        ).join(", ") || ""}
                        onChange={(e) => {
                          const open_box = e.target.value.split(",").map(s => {
                            const trimmed = s.trim();
                            if (trimmed.includes("-")) {
                              const [inicio, fim] = trimmed.split("-");
                              return { inicio: inicio?.trim() || "", fim: fim?.trim() || "" };
                            } else {
                              return { inicio: trimmed, fim: trimmed };
                            }
                          }).filter(a => a.inicio);
                          setSchedule(prev => ({
                            ...prev,
                            [day]: { ...prev[day], open_box }
                          }));
                        }}
                        className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm focus:border-[#A6C48A] focus:outline-none"
                        placeholder="07:00, 10:00-13:45, 17:30-21:00"
                        rows={2}
                      />
                      <p className="text-[#8A8A8A] text-xs mt-1">Formato: HH:MM ou HH:MM-HH:MM</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* PRICES TAB */}
        {activeTab === "prices" && (
          <section>
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-[#F5F5F5]" >
                <DollarSign className="inline mr-2 text-[#A6C48A]" size={24} /> Preços
              </h2>
              <button onClick={handleSavePrices} className="flex items-center gap-2 bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] px-4 py-2 rounded" data-testid="save-prices-btn">
                <Save size={18} /> Guardar
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-1">Preço de referência</h3>
                <p className="text-[#8A8A8A] text-xs mb-3">Aparece no topo do site por baixo dos botões (ex.: “45 € por mês”). Vazio = não aparece.</p>
                <input
                  value={prices.preco_desde || ""}
                  onChange={(e) => setPrices((prev) => ({ ...prev, preco_desde: e.target.value }))}
                  placeholder="Ex.: 45 € por mês"
                  className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm"
                />
              </div>

              {/* Inscrição */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-3">Inscrição</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1">Joia (€)</label>
                    <input type="number" placeholder="Ex.: 30" value={prices.inscricao?.joia || ""} onChange={(e) => setPrices(prev => ({ ...prev, inscricao: { ...prev.inscricao, joia: Number(e.target.value) } }))} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" />
                  </div>
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1">Seguro Anual (€)</label>
                    <input type="number" placeholder="Ex.: 15" value={prices.inscricao?.seguro_anual || ""} onChange={(e) => setPrices(prev => ({ ...prev, inscricao: { ...prev.inscricao, seguro_anual: Number(e.target.value) } }))} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" />
                  </div>
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1">Curso Iniciação (€)</label>
                    <input type="number" placeholder="Ex.: 20" value={prices.inscricao?.curso_iniciacao || ""} onChange={(e) => setPrices(prev => ({ ...prev, inscricao: { ...prev.inscricao, curso_iniciacao: Number(e.target.value) } }))} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" />
                  </div>
                </div>
              </div>

              {/* Mensalidades */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-3">Mensalidades</h3>
                {prices.mensalidades?.map((m, i) => (
                  <div key={i} className="mb-4 p-3 bg-[#141414] rounded border border-[#3A3A3A]">
                    <div className="grid md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Nome</label>
                        <RichTextEditor 
                          value={m.nome} 
                          onChange={(val) => { const arr = [...prices.mensalidades]; arr[i].nome = val; setPrices(prev => ({ ...prev, mensalidades: arr })); }} 
                          placeholder="Ex.: Mensal ilimitado"
                          rows={1}
                        />
                      </div>
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Preço (€)</label>
                        <input type="number" placeholder="Ex.: 45" value={m.preco} onChange={(e) => { const arr = [...prices.mensalidades]; arr[i].preco = Number(e.target.value); setPrices(prev => ({ ...prev, mensalidades: arr })); }} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="block text-[#A1A1AA] text-xs mb-1">Descrição</label>
                      <RichTextEditor 
                        value={m.descricao} 
                        onChange={(val) => { const arr = [...prices.mensalidades]; arr[i].descricao = val; setPrices(prev => ({ ...prev, mensalidades: arr })); }} 
                        placeholder="Ex.: Acesso a todas as aulas e ao treino livre"
                        rows={2}
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      <label className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                        <input type="checkbox" checked={m.popular || false} onChange={(e) => { const arr = [...prices.mensalidades]; arr[i].popular = e.target.checked; setPrices(prev => ({ ...prev, mensalidades: arr })); }} className="rounded" />
                        Destacar como "Mais Popular"
                      </label>
                      <button onClick={() => { const arr = prices.mensalidades.filter((_, idx) => idx !== i); setPrices(prev => ({ ...prev, mensalidades: arr })); }} className="p-2 bg-red-600 text-white rounded"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
                <button onClick={() => setPrices(prev => ({ ...prev, mensalidades: [...(prev.mensalidades || []), { nome: "", preco: 0, periodo: "mês", descricao: "", popular: false }] }))} className="text-[#A6C48A] text-sm mt-2">+ Adicionar mensalidade</button>
              </div>

              {/* Drop-in */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-3">Drop-in / Packs</h3>
                {prices.drop_in?.map((d, i) => (
                  <div key={i} className="mb-3 p-3 bg-[#141414] rounded border border-[#3A3A3A]">
                    <div className="grid md:grid-cols-2 gap-3 mb-2">
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Nome</label>
                        <RichTextEditor 
                          value={d.nome} 
                          onChange={(val) => { const arr = [...prices.drop_in]; arr[i].nome = val; setPrices(prev => ({ ...prev, drop_in: arr })); }} 
                          placeholder="Ex.: Aula avulsa ou pack de 10 aulas"
                          rows={1}
                        />
                      </div>
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className="block text-[#A1A1AA] text-xs mb-1">Preço (€)</label>
                          <input type="number" placeholder="Ex.: 12" value={d.preco} onChange={(e) => { const arr = [...prices.drop_in]; arr[i].preco = Number(e.target.value); setPrices(prev => ({ ...prev, drop_in: arr })); }} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" />
                        </div>
                        <button onClick={() => { const arr = prices.drop_in.filter((_, idx) => idx !== i); setPrices(prev => ({ ...prev, drop_in: arr })); }} className="p-2 bg-red-600 text-white rounded"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  </div>
                ))}
                <button onClick={() => setPrices(prev => ({ ...prev, drop_in: [...(prev.drop_in || []), { nome: "", preco: 0 }] }))} className="text-[#A6C48A] text-sm mt-2">+ Adicionar pack</button>
              </div>

              {/* Serviços Extra */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-3">Serviços Extra</h3>
                {prices.servicos_extra?.map((s, i) => (
                  <div key={i} className="mb-3 p-3 bg-[#141414] rounded border border-[#3A3A3A]">
                    <div className="grid md:grid-cols-2 gap-3 mb-2">
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Nome</label>
                        <RichTextEditor 
                          value={s.nome} 
                          onChange={(val) => { const arr = [...prices.servicos_extra]; arr[i].nome = val; setPrices(prev => ({ ...prev, servicos_extra: arr })); }} 
                          placeholder="Ex.: Consulta de nutrição"
                          rows={1}
                        />
                      </div>
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className="block text-[#A1A1AA] text-xs mb-1">Preço (ou texto)</label>
                          <input placeholder="Ex.: 40 ou Sob consulta" value={s.preco} onChange={(e) => { const arr = [...prices.servicos_extra]; arr[i].preco = isNaN(e.target.value) ? e.target.value : Number(e.target.value); setPrices(prev => ({ ...prev, servicos_extra: arr })); }} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" />
                        </div>
                        <button onClick={() => { const arr = prices.servicos_extra.filter((_, idx) => idx !== i); setPrices(prev => ({ ...prev, servicos_extra: arr })); }} className="p-2 bg-red-600 text-white rounded"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  </div>
                ))}
                <button onClick={() => setPrices(prev => ({ ...prev, servicos_extra: [...(prev.servicos_extra || []), { nome: "", preco: 0 }] }))} className="text-[#A6C48A] text-sm mt-2">+ Adicionar serviço</button>
              </div>
            </div>
          </section>
        )}

        {/* COACHES TAB */}
        {activeTab === "coaches" && (
          <section>
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-[#F5F5F5]" >
                <Users className="inline mr-2 text-[#A6C48A]" size={24} /> Equipa
              </h2>
              <button onClick={() => setShowAddCoach(true)} className="flex items-center gap-2 bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] px-4 py-2 rounded" data-testid="add-coach-btn">
                <Plus size={18} /> Adicionar Coach
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {coaches.length === 0 ? (
                <div className="col-span-full text-center py-12 text-[#A1A1AA]">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Nenhum coach adicionado.</p>
                </div>
              ) : (
                coaches.map((coach) => (
                  <div key={coach.id} className={`bg-[#1F1F1F] rounded-lg overflow-hidden border ${coach.active ? "border-[#3A3A3A]" : "border-red-500/30 opacity-60"}`}>
                    <div className="aspect-square bg-[#141414] relative">
                      {coach.image ? (
                        <img src={coach.image} alt={stripHtml(coach.nome)} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#A1A1AA]">
                          <Users size={64} />
                        </div>
                      )}
                      {!coach.active && <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">Inativo</div>}
                    </div>
                    <div className="p-4">
                      <h3 className="text-[#F5F5F5] font-semibold">{stripHtml(coach.nome)}</h3>
                      <p className="text-[#A6C48A] text-sm">{stripHtml(coach.role)}</p>
                      {stripHtml(coach.frase) && <p className="text-[#A1A1AA] text-xs mt-1 italic">“{stripHtml(coach.frase)}”</p>}
                      <div className="flex gap-2 mt-4">
                        <button onClick={() => setEditingCoach(coach)} className="flex-1 bg-[#3A3A3A] hover:bg-[#4A4A4A] text-[#F5F5F5] py-2 rounded text-sm">Editar</button>
                        <button onClick={() => handleUpdateCoach({ ...coach, active: !coach.active })} className={`p-2 rounded ${coach.active ? "bg-[#3A3A3A]" : "bg-green-600"} text-white`}>
                          {coach.active ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        <button onClick={() => handleDeleteCoach(coach.id)} className="p-2 bg-red-600 hover:bg-red-700 text-white rounded">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        )}

        {/* CONTENT TAB */}
        {activeTab === "content" && (
          <section>
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-[#F5F5F5]" >
                <FileText className="inline mr-2 text-[#A6C48A]" size={24} /> Conteúdo do Site
              </h2>
              <button onClick={handleSaveContent} className="flex items-center gap-2 bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] px-4 py-2 rounded" data-testid="save-content-btn">
                <Save size={18} /> Guardar
              </button>
            </div>

            <div className="space-y-6">
              <ButtonTextsEditor content={content} setContent={setContent} />

              {/* Hero Section */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-3 flex items-center gap-2"><Home size={18} /> Secção Hero</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1">Título Principal</label>
                    <RichTextEditor 
                      value={content.hero_title || ""} 
                      onChange={(val) => setContent(prev => ({ ...prev, hero_title: val }))} 
                      placeholder={ph("hero_title")}
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1">Subtítulo</label>
                    <RichTextEditor 
                      value={content.hero_subtitle || ""} 
                      onChange={(val) => setContent(prev => ({ ...prev, hero_subtitle: val }))} 
                      placeholder={ph("hero_subtitle")}
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#A1A1AA] text-xs mb-1">Botão Principal</label>
                      <RichTextEditor 
                        value={content.hero_button_primary || ""} 
                        onChange={(val) => setContent(prev => ({ ...prev, hero_button_primary: val }))} 
                        placeholder={ph("hero_button_primary")}
                        rows={1}
                      />
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-xs mb-1">Botão Secundário</label>
                      <RichTextEditor 
                        value={content.hero_button_secondary || ""} 
                        onChange={(val) => setContent(prev => ({ ...prev, hero_button_secondary: val }))} 
                        placeholder={ph("hero_button_secondary")}
                        rows={1}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-3 flex items-center gap-2"><Settings size={18} /> Rodapé</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1">Frase do Rodapé</label>
                    <RichTextEditor 
                      value={content.footer_tagline || ""} 
                      onChange={(val) => setContent(prev => ({ ...prev, footer_tagline: val }))} 
                      placeholder={ph("footer_tagline")}
                      rows={1}
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#A1A1AA] text-xs mb-1">Texto do Copyright</label>
                      <RichTextEditor 
                        value={content.footer_text || ""} 
                        onChange={(val) => setContent(prev => ({ ...prev, footer_text: val }))} 
                        placeholder={ph("footer_text")}
                        rows={1}
                      />
                    </div>
                    <div>
                      <label className="block text-[#A1A1AA] text-xs mb-1">Horário de Funcionamento</label>
                      <RichTextEditor 
                        value={content.opening_hours || ""} 
                        onChange={(val) => setContent(prev => ({ ...prev, opening_hours: val }))} 
                        placeholder="Ex.: Seg-Sex 07:00-21:00 · Sáb 09:00-13:00 (vazio = não aparece)"
                        rows={1}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section Titles and Subtitles */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-3 flex items-center gap-2"><FileText size={18} /> Títulos e Subtítulos das Secções</h3>
                <p className="text-[#8A8A8A] text-xs mb-4">Use o botão de cor (paleta) para destacar palavras em verde.</p>
                <div className="space-y-6">
                  {/* Horários */}
                  <div className="border-b border-[#3A3A3A] pb-4">
                    <h4 className="text-[#F5F5F5] text-sm font-semibold mb-3">Secção Horários</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Título</label>
                        <RichTextEditor 
                          value={content.schedule_title || ""} 
                          onChange={(val) => setContent(prev => ({ ...prev, schedule_title: val }))} 
                          placeholder={ph("schedule_title")}
                          rows={1}
                        />
                      </div>
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Subtítulo</label>
                        <RichTextEditor 
                          value={content.schedule_subtitle || ""} 
                          onChange={(val) => setContent(prev => ({ ...prev, schedule_subtitle: val }))} 
                          placeholder={ph("schedule_subtitle")}
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Equipa */}
                  <div className="border-b border-[#3A3A3A] pb-4">
                    <h4 className="text-[#F5F5F5] text-sm font-semibold mb-3">Secção Equipa</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Título</label>
                        <RichTextEditor 
                          value={content.coaches_title || ""} 
                          onChange={(val) => setContent(prev => ({ ...prev, coaches_title: val }))} 
                          placeholder={ph("coaches_title")}
                          rows={1}
                        />
                      </div>
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Subtítulo</label>
                        <RichTextEditor 
                          value={content.coaches_subtitle || ""} 
                          onChange={(val) => setContent(prev => ({ ...prev, coaches_subtitle: val }))} 
                          placeholder={ph("coaches_subtitle")}
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preços */}
                  <div className="border-b border-[#3A3A3A] pb-4">
                    <h4 className="text-[#F5F5F5] text-sm font-semibold mb-3">Secção Preços</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Título</label>
                        <RichTextEditor 
                          value={content.pricing_title || ""} 
                          onChange={(val) => setContent(prev => ({ ...prev, pricing_title: val }))} 
                          placeholder={ph("pricing_title")}
                          rows={1}
                        />
                      </div>
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Subtítulo</label>
                        <RichTextEditor 
                          value={content.pricing_subtitle || ""} 
                          onChange={(val) => setContent(prev => ({ ...prev, pricing_subtitle: val }))} 
                          placeholder={ph("pricing_subtitle")}
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contacto */}
                  <div className="border-b border-[#3A3A3A] pb-4">
                    <h4 className="text-[#F5F5F5] text-sm font-semibold mb-3">Secção Contacto</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Título</label>
                        <RichTextEditor 
                          value={content.contact_title || ""} 
                          onChange={(val) => setContent(prev => ({ ...prev, contact_title: val }))} 
                          placeholder={ph("contact_title")}
                          rows={1}
                        />
                      </div>
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Subtítulo</label>
                        <RichTextEditor 
                          value={content.contact_subtitle || ""} 
                          onChange={(val) => setContent(prev => ({ ...prev, contact_subtitle: val }))} 
                          placeholder={ph("contact_subtitle")}
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Instagram */}
                  <div>
                    <h4 className="text-[#F5F5F5] text-sm font-semibold mb-3">Secção Instagram</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Título</label>
                        <RichTextEditor 
                          value={content.instagram_title || ""} 
                          onChange={(val) => setContent(prev => ({ ...prev, instagram_title: val }))} 
                          placeholder={ph("instagram_title")}
                          rows={1}
                        />
                      </div>
                      <div>
                        <label className="block text-[#A1A1AA] text-xs mb-1">Subtítulo (opcional)</label>
                        <RichTextEditor 
                          value={content.instagram_subtitle || ""} 
                          onChange={(val) => setContent(prev => ({ ...prev, instagram_subtitle: val }))} 
                          placeholder="Opcional. Ex.: Treinos, eventos e novidades da semana"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* LAYOUT TAB */}
        {activeTab === "layout" && (
          <section>
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-[#F5F5F5]">
                <Settings className="inline mr-2 text-[#A6C48A]" size={24} /> Layout das Secções
              </h2>
              <button
                onClick={handleSaveContent}
                className="bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] font-bold px-6 py-2 rounded flex items-center gap-2"
              >
                <Save size={18} /> Guardar
              </button>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-6">
              Ordena as secções da página inicial (↑/↓) e liga/desliga cada uma (olho).
              As alterações só ficam ativas depois de clicares em <strong>Guardar</strong>.
            </p>
            <div className="space-y-2 max-w-2xl">
              {getLayoutSections().map((s, index) => {
                const meta = SECTION_META.find((m) => m.key === s.key);
                const list = getLayoutSections();
                return (
                  <div
                    key={s.key}
                    className="flex items-center justify-between bg-[#1F1F1F] border border-[#3A3A3A] rounded px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-[#8A8A8A] text-sm w-6">{index + 1}</span>
                      <span
                        className={`font-semibold ${
                          s.visible ? "text-[#F5F5F5]" : "text-[#8A8A8A] line-through"
                        }`}
                      >
                        {meta ? meta.label : s.key}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveSection(index, -1)}
                        disabled={index === 0}
                        className="p-2 text-[#A1A1AA] hover:text-[#A6C48A] disabled:opacity-30"
                        title="Subir"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveSection(index, 1)}
                        disabled={index === list.length - 1}
                        className="p-2 text-[#A1A1AA] hover:text-[#A6C48A] disabled:opacity-30"
                        title="Descer"
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => toggleSection(index)}
                        className="p-2 text-[#A1A1AA] hover:text-[#A6C48A]"
                        title={s.visible ? "Esconder" : "Mostrar"}
                      >
                        {s.visible ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <h3 className="text-xl font-bold text-[#F5F5F5] mt-10 mb-2">Cores do site</h3>
            <p className="text-[#A1A1AA] text-sm mb-4">
              Escuro: fundo negro com o verde da marca. Claro: fundo off-white com texto negro. Clica em <strong>Guardar</strong> (em cima) para aplicar.
            </p>
            <div className="flex gap-2 mb-2">
              {[
                { key: "dark", label: "Escuro", swatch: "bg-[#010000] border-[#A6C48A]" },
                { key: "light", label: "Claro", swatch: "bg-[#FEF6F6] border-[#010000]" },
              ].map((m) => {
                const active = (content.theme_mode || "dark") === m.key;
                return (
                  <button
                    key={m.key}
                    onClick={() => setContent((prev) => ({ ...prev, theme_mode: m.key }))}
                    className={`flex items-center gap-2 px-4 py-2 rounded border text-sm ${active ? "border-[#A6C48A] text-[#A6C48A]" : "border-[#3A3A3A] text-[#A1A1AA] hover:border-[#4A4A4A]"}`}
                  >
                    <span className={`w-4 h-4 rounded-full border-2 ${m.swatch}`} /> {m.label}
                  </button>
                );
              })}
            </div>
            <FontPicker content={content} setContent={setContent} />
            <h3 className="text-xl font-bold text-[#F5F5F5] mt-10 mb-2">Espaçamento</h3>
            <p className="text-[#A1A1AA] text-sm mb-4">
              Densidade do espaço entre secções. Clica em <strong>Guardar</strong> (em cima) para aplicar.
            </p>
            <div className="flex gap-2">
              {[
                { key: "compact", label: "Compacto" },
                { key: "normal", label: "Normal" },
                { key: "spacious", label: "Espaçoso" },
              ].map((d) => {
                const current = content.theme_density || "normal";
                return (
                  <button
                    key={d.key}
                    onClick={() => setContent((prev) => ({ ...prev, theme_density: d.key }))}
                    className={`px-4 py-2 rounded border text-sm transition-colors ${
                      current === d.key
                        ? "bg-[#A6C48A] border-[#A6C48A] text-[#010000]"
                        : "border-[#3A3A3A] text-[#A1A1AA] hover:border-[#A6C48A]"
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* USERS TAB */}
        {activeTab === "users" && (
          <section>
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-[#F5F5F5]">
                <Users className="inline mr-2 text-[#A6C48A]" size={24} /> Utilizadores
              </h2>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-6">
              Cria acessos adicionais ao painel (todos com acesso total). Cada pessoa entra com o seu utilizador e password.
            </p>

            <ChangePasswordBox apiUrl={API_URL} getAuthHeader={getAuthHeader} onAuthError={handleAuthError} />

            <div className="bg-[#1F1F1F] border border-[#3A3A3A] rounded p-4 mb-6 max-w-2xl">
              <h3 className="text-[#F5F5F5] font-semibold mb-3">Novo utilizador</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser((p) => ({ ...p, username: e.target.value }))}
                  placeholder="Utilizador (ex.: marta)"
                  className="flex-1 bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm"
                />
                <input
                  type="text"
                  value={newUser.password}
                  onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
                  placeholder="Password (mín. 6)"
                  className="flex-1 bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm"
                />
                <button
                  onClick={handleCreateUser}
                  className="bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] font-bold px-4 py-2 rounded flex items-center gap-2 justify-center"
                >
                  <Plus size={18} /> Criar
                </button>
              </div>
            </div>

            <div className="space-y-2 max-w-2xl">
              {users.length === 0 ? (
                <p className="text-[#8A8A8A] text-sm">Sem utilizadores para mostrar.</p>
              ) : (
                users.map((u) => (
                  <div
                    key={u.username}
                    className="flex items-center justify-between bg-[#1F1F1F] border border-[#3A3A3A] rounded px-4 py-3"
                  >
                    <span className="text-[#F5F5F5] font-semibold">{u.username}</span>
                    <button
                      onClick={() => handleDeleteUser(u.username)}
                      className="p-2 text-[#A1A1AA] hover:text-red-400"
                      title="Remover"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </section>
        )}

        {/* CONTACT TAB */}
        {activeTab === "contact" && (
          <section>
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-[#F5F5F5]" >
                <Phone className="inline mr-2 text-[#A6C48A]" size={24} /> Contacto & Redes Sociais
              </h2>
              <button onClick={handleSaveContent} className="flex items-center gap-2 bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] px-4 py-2 rounded" data-testid="save-contact-btn">
                <Save size={18} /> Guardar
              </button>
            </div>

            <div className="space-y-6">
              {/* Contact Info */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-3">Informações de Contacto</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1 flex items-center gap-1"><Phone size={12} /> Telefone</label>
                    <input value={content.contact_phone || ""} onChange={(e) => setContent(prev => ({ ...prev, contact_phone: e.target.value }))} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" placeholder={ph("contact_phone")} />
                  </div>
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1 flex items-center gap-1"><Mail size={12} /> Email</label>
                    <input value={content.contact_email || ""} onChange={(e) => setContent(prev => ({ ...prev, contact_email: e.target.value }))} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" placeholder="Ex.: nome@gmail.com (vazio = não aparece)" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[#A1A1AA] text-xs mb-1 flex items-center gap-1"><MapPin size={12} /> Morada</label>
                    <input value={content.contact_address || ""} onChange={(e) => setContent(prev => ({ ...prev, contact_address: e.target.value }))} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" placeholder={ph("contact_address")} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[#A1A1AA] text-xs mb-1 flex items-center gap-1"><MapPin size={12} /> Link Google Maps</label>
                    <input value={content.contact_map_url || ""} onChange={(e) => setContent(prev => ({ ...prev, contact_map_url: e.target.value }))} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" placeholder="Ex.: https://maps.app.goo.gl/... (vazio = sem link de mapa)" />
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-[#1F1F1F] rounded-lg p-4 border border-[#3A3A3A]">
                <h3 className="text-[#A6C48A] font-bold mb-3">Redes Sociais</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1 flex items-center gap-1"><MessageCircle size={12} /> WhatsApp</label>
                    <input value={content.whatsapp_number || ""} onChange={(e) => setContent(prev => ({ ...prev, whatsapp_number: e.target.value }))} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" placeholder={ph("whatsapp_number")} />
                  </div>
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1 flex items-center gap-1"><Instagram size={12} /> Instagram URL</label>
                    <input value={content.instagram_url || ""} onChange={(e) => setContent(prev => ({ ...prev, instagram_url: e.target.value }))} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" placeholder={ph("instagram_url")} />
                  </div>
                  <div>
                    <label className="block text-[#A1A1AA] text-xs mb-1">Facebook URL</label>
                    <input value={content.facebook_url || ""} onChange={(e) => setContent(prev => ({ ...prev, facebook_url: e.target.value }))} className="w-full bg-[#141414] border border-[#3A3A3A] rounded px-3 py-2 text-[#F5F5F5] text-sm" placeholder="Ex.: https://www.facebook.com/... (vazio = não aparece)" />
                  </div>
                </div>
              </div>
              <LeadFormEditor content={content} setContent={setContent} />
            </div>
          </section>
        )}
      </main>
      </div>

      {/* MODALS */}
      
      {/* Add Coach Modal */}
      {showAddCoach && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1F1F1F] rounded-lg p-6 w-full max-w-lg border border-[#3A3A3A] max-h-[90vh] overflow-y-auto">
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h3 className="text-xl font-bold text-[#F5F5F5]">Novo Coach</h3>
              <button onClick={() => setShowAddCoach(false)} className="text-[#A1A1AA] hover:text-[#F5F5F5]"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Nome *</label>
                <RichTextEditor 
                  value={newCoach.nome} 
                  onChange={(val) => setNewCoach(prev => ({ ...prev, nome: val }))} 
                  placeholder="Ex.: Ana Costa"
                  rows={1}
                />
              </div>
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Função *</label>
                <RichTextEditor 
                  value={newCoach.role} 
                  onChange={(val) => setNewCoach(prev => ({ ...prev, role: val }))} 
                  placeholder="Ex.: Fisioterapeuta, Personal trainer, Nutricionista"
                  rows={1}
                />
              </div>
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Frase/Motto</label>
                <RichTextEditor 
                  value={newCoach.frase} 
                  onChange={(val) => setNewCoach(prev => ({ ...prev, frase: val }))} 
                  placeholder="Opcional. Ex.: Treinar é cuidar de ti."
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Foto</label>
                <div className="flex gap-2">
                  <input type="text" value={newCoach.image} onChange={(e) => setNewCoach(prev => ({ ...prev, image: e.target.value }))} className="flex-1 bg-[#141414] border border-[#3A3A3A] rounded px-4 py-2 text-[#F5F5F5] text-sm" placeholder="Carrega com Upload ou cola um link https://" />
                  <label className="flex items-center gap-2 bg-[#3A3A3A] hover:bg-[#4A4A4A] text-[#F5F5F5] px-4 py-2 rounded cursor-pointer">
                    <Upload size={16} /> Upload
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, (url) => setNewCoach(prev => ({ ...prev, image: url })))} className="hidden" />
                  </label>
                </div>
                {newCoach.image && <img src={newCoach.image} alt="Preview" className="mt-2 h-24 w-24 object-cover bg-[#141414] rounded" />}
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowAddCoach(false)} className="flex-1 bg-[#3A3A3A] hover:bg-[#4A4A4A] text-[#F5F5F5] py-2 rounded">Cancelar</button>
                <button onClick={handleAddCoach} className="flex-1 bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] py-2 rounded" data-testid="save-new-coach-btn"><Save size={16} className="inline mr-2" />Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Coach Modal */}
      {editingCoach && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1F1F1F] rounded-lg p-6 w-full max-w-lg border border-[#3A3A3A] max-h-[90vh] overflow-y-auto">
            <div className="sticky top-[65px] z-40 -mx-4 md:-mx-8 px-4 md:px-8 py-3 mb-6 bg-[#141414]/95 backdrop-blur border-b border-[#3A3A3A] flex items-center justify-between gap-4">
              <h3 className="text-xl font-bold text-[#F5F5F5]">Editar Coach</h3>
              <button onClick={() => setEditingCoach(null)} className="text-[#A1A1AA] hover:text-[#F5F5F5]"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Nome</label>
                <RichTextEditor 
                  value={editingCoach.nome} 
                  onChange={(val) => setEditingCoach(prev => ({ ...prev, nome: val }))} 
                  placeholder="Ex.: Ana Costa"
                  rows={1}
                />
              </div>
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Função</label>
                <RichTextEditor 
                  value={editingCoach.role} 
                  onChange={(val) => setEditingCoach(prev => ({ ...prev, role: val }))} 
                  placeholder="Ex.: Fisioterapeuta, Personal trainer, Nutricionista"
                  rows={1}
                />
              </div>
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Frase/Motto</label>
                <RichTextEditor 
                  value={editingCoach.frase || ""} 
                  onChange={(val) => setEditingCoach(prev => ({ ...prev, frase: val }))} 
                  placeholder="Opcional. Ex.: Treinar é cuidar de ti."
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Foto</label>
                <div className="flex gap-2">
                  <input type="text" value={editingCoach.image || ""} onChange={(e) => setEditingCoach(prev => ({ ...prev, image: e.target.value }))} className="flex-1 bg-[#141414] border border-[#3A3A3A] rounded px-4 py-2 text-[#F5F5F5] text-sm" placeholder="Carrega com Upload ou cola um link https://" />
                  <label className="flex items-center gap-2 bg-[#3A3A3A] hover:bg-[#4A4A4A] text-[#F5F5F5] px-4 py-2 rounded cursor-pointer">
                    <Upload size={16} /> Upload
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, (url) => setEditingCoach(prev => ({ ...prev, image: url })))} className="hidden" />
                  </label>
                </div>
                {editingCoach.image && <img src={editingCoach.image} alt="Preview" className="mt-2 h-24 w-24 object-cover bg-[#141414] rounded" />}
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setEditingCoach(null)} className="flex-1 bg-[#3A3A3A] hover:bg-[#4A4A4A] text-[#F5F5F5] py-2 rounded">Cancelar</button>
                <button onClick={() => handleUpdateCoach(editingCoach)} className="flex-1 bg-[#A6C48A] hover:bg-[#B9D3A1] text-[#010000] py-2 rounded"><Save size={16} className="inline mr-2" />Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
